<script lang="ts">
	import type { LandingItemContext, PrimaryResultFilter, ResultFilters, SecondaryResultFilter } from '$lib/def/landing';
	import type { Options } from '$lib/serv/vendor-integration/splide';
	import type { Cache } from "$lib/serv/core/cache";
	import type { Page } from '@sveltejs/kit';
	
	import { getDB, KEY_PRIMARY_FILTER, KEY_SECONDARY_FILTER, SPKEYS } from "$lib/serv/food-landing";
	import { heightFromResizeObserver } from '$lib/util/obs';
	import { EMPTY, map, of, switchMap, tap, type Observable } from "rxjs";
	import { LOCATION_PROPS } from '$lib/serv/locations';
	import { fetch$ } from "$lib/serv/core/cache";
	import { Vertical } from '$lib/def/verticals';
	import { queryFrom } from '$lib/util/url';
	import { getContext } from "svelte";

	import LazyBackgroundImage from '$lib/ui/shared/LazyBackgroundImage.svelte';
	import CarouselV3 from '$lib/ui/layout/CarouselV3.svelte';
	import LazyImage from '$lib/ui/shared/LazyImage.svelte';
	import Header from '$lib/ui/shared/Header.svelte';
	import Grid from '$lib/ui/layout/Grid.svelte';
	
	export let endpoint: string | undefined;
	export let searchParams: Record<string, string>;
	export let onLoadedPrimaryFilters: (filters: PrimaryResultFilter[]) => void;
	
	const { base, cacheAge } = getContext('endpoints') as LandingItemContext ?? {};
	
	let options: Options = {
		// prevNextButtons: false,
		// pageDots: false,
		// wrapAround: false
		autoWidth: true,
		perPage: 1,
		perMove: 1,
		arrows: false,
		breakpoints: {},
	};

	$: cache = endpoint ? createObs(endpoint, searchParams) : EMPTY;

	function createObs(path:string, params: typeof searchParams): Observable<Cache<ResultFilters> & { active: PrimaryResultFilter | undefined }> {
		if (!path) return EMPTY;
		return fetch$<ResultFilters>(new URL(base + path), SPKEYS, { db: getDB(), msMaxCacheAge: cacheAge.medium }).pipe(
			switchMap(cache => of(params).pipe(map((params) => ({ cache, params })))),
			tap(({cache:{payload}, params}) => payload?.primary_filters && onLoadedPrimaryFilters(payload.primary_filters)),
			map(({cache, params}) => {
				const makeID = (item: PrimaryResultFilter) => item.type === 'filter' ? item.term as string : item.type;
				return {
					payload: {
						secondary_filters: cache.payload?.secondary_filters ?? [],
						// Exclude SnackMe because we don't cover that yet on the web app
						primary_filters: cache.payload?.primary_filters.filter((item) => item.name.toLowerCase() !== 'snackme vouchers') ?? [],
					},
					active: cache.payload?.primary_filters.find((item) => queryFrom(params).hasValue(KEY_PRIMARY_FILTER, makeID(item))),
					loading: cache.loading,
					error: cache.error
				};
			}),
		);
	}

	let resizeHeight$;
	const initHeightObserver = (el: HTMLElement) => { resizeHeight$ = heightFromResizeObserver(el) };

	const makeID = (item: PrimaryResultFilter | SecondaryResultFilter) => item.type === 'filter' ? item.term as string : item.type;
</script>

{#if $cache}

	<div class="filters">
		<div class="animate-height" style:height={`${$resizeHeight$}px`}>
			<div use:initHeightObserver>

				{#if $cache?.active}

					<Header>
						<div class="filter">
							<LazyImage image={[$cache.active.main_image, 160]} alt={$cache.active.name} />
							<h3 class="display-l bold">{$cache.active.name}</h3>
						</div>
					</Header>

				{:else}

					<div class="mobile-only">
						<!--<h3 class="headline-m bold>">{vertical === Vertical.Store ? 'Store' : 'Food'} types</h3>-->
						<CarouselV3 cache={$cache} type="filters primary" key={(payload: { primary_filters: PrimaryResultFilter[] }) => payload?.primary_filters}>
							{#snippet children({ item: filter })}
								{#if filter.type === "link"} <!-- The exceptions: "filters" that are actually links to a different place on the app -->
									{#if filter.name === "Pick n Pay Groceries"}
										<a href={queryFrom(searchParams).keep(...LOCATION_PROPS).set("vertical", Vertical.Grocery).toUrl()} class="item">
											<LazyBackgroundImage image={[filter.main_image, 160, 0]} alt={`${filter.name} near me`} />
											<div class="label-xl medium">{filter.name}</div>
										</a>
									{/if}
								{:else}
									<a
										href={queryFrom(searchParams).toggle( KEY_PRIMARY_FILTER, makeID(filter)).toUrl()}
										class:active={queryFrom(searchParams).hasValue(KEY_PRIMARY_FILTER, makeID(filter))}
										class="item"
										data-sveltekit-replacestate={queryFrom(searchParams).has(KEY_PRIMARY_FILTER)}
									>
										<LazyBackgroundImage image={[filter.main_image, 160, 0]} alt={`${filter.name} near me`} />
										<div class="label-m medium ellipses-2">{filter.name}</div>
									</a>
								{/if}
							{/snippet}
						</CarouselV3>
					</div>

					<div class="desktop-only">
						<Grid cache={$cache} type="filters" key={(payload) => payload?.primary_filters} let:item={filter}>
							{#if filter.type === "link"} <!-- The exceptions: "filters" that are actually links to a different place on the app -->
								{#if filter.name === "Pick n Pay Groceries"}
									<a href={queryFrom(searchParams).keep(...LOCATION_PROPS).set("vertical", Vertical.Grocery).toUrl()} class="item">
										<LazyBackgroundImage image={[filter.main_image, 160, 0]} alt={`${filter.name} near me`} />
										<div class="label-xl medium">{filter.name}</div>
									</a>
								{/if}
							{:else}
								<a
									href={queryFrom(searchParams).toggle( KEY_PRIMARY_FILTER, makeID(filter)).toUrl()}
									class:active={queryFrom(searchParams).hasValue(KEY_PRIMARY_FILTER, makeID(filter))}
									class="item"
									data-sveltekit-replacestate={queryFrom(searchParams).has(KEY_PRIMARY_FILTER)}
								>
									<LazyBackgroundImage image={[filter.main_image, 160, 0]} alt={`${filter.name} near me`} />
									<div class="label-xl medium">{filter.name}</div>
								</a>
							{/if}
						</Grid>
					</div>

				{/if}

				{#if searchParams?.[KEY_PRIMARY_FILTER] !== "deals"}

					<div class="pills label-xl book">
						<CarouselV3 cache={$cache} {options} type="filters secondary" key={(payload: { secondary_filters: SecondaryResultFilter[] }) => payload?.secondary_filters}>
							{#snippet children({ item: filter })}
								<a
									href={queryFrom(searchParams).subtoggle(KEY_SECONDARY_FILTER, makeID(filter)).toUrl()}
									class:active={queryFrom(searchParams).hasValueInList(KEY_SECONDARY_FILTER, makeID(filter))}
									class="label-xl book"
									data-sveltekit-replacestate={queryFrom(searchParams).has(KEY_SECONDARY_FILTER)}
								>
									<div class="name">{filter.name}</div>
								</a>
							{/snippet}
						</CarouselV3>
					</div>

				{/if}
				
			</div>
		</div>
	</div>
{/if}

<style lang="scss">
	@use 'margin';
	@use 'ease';

	.filters { @include margin.large; }

	.animate-height {
		// overflow: hidden;
		transition: height 300ms ease.$out-expo;
	}
</style>
